import React, { useMemo, useCallback } from 'react';
import { debounce, escape, unescape } from 'lodash/fp';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import TextEditor, { TEXT_EDITOR_VARIANTS } from 'ui/TextEditor';
import TextArea from 'components/shared/debouncedTextArea';
import { LabeledCheckbox } from 'components/shared/checkbox';
import { useFeatureFlags } from 'components/shared/hooks';
import ElementBase from '../base';
import ImageView from '../shared/ImageView';

function RevealElement(props) {
  const {
    id,
    content,
    title,
    applyChanges,
    handleUpdate,
    handleImageChange,
    toggleAddToImageLibrary,
  } = props;
  const {
    subject,
    instruction,
    body,
    content_image: image,
    alt_content_image: altImage,
    scrollable,
  } = content;

  const hasScrollFeatureFlag = useFeatureFlags([
    'readiness-enable-reveal-card-toggle-scrolling',
  ]);

  const debouncedHandleUpdate = useMemo(
    () => debounce(300, handleUpdate),
    [handleUpdate],
  );

  const onSubjectChange = useCallback(
    text => {
      const changes = { subject: text || '' };
      applyChanges(changes);
      debouncedHandleUpdate(changes);
    },
    [applyChanges, debouncedHandleUpdate],
  );

  const onBodyChange = useCallback(
    text => {
      const changes = { body: text || '' };
      applyChanges(changes);
      debouncedHandleUpdate(changes);
    },
    [applyChanges, debouncedHandleUpdate],
  );

  const onScrollChange = isChecked => {
    const changes = { scrollable: isChecked };
    applyChanges(changes);
    debouncedHandleUpdate(changes);
  };

  return (
    <div className="EditorElement-content RevealElement">
      <div className="EditorElement-header">
        <div className="EditorElement-title" children={title} />
        {hasScrollFeatureFlag && (
          <LabeledCheckbox
            label={
              <Text
                className="EditorElement-header--label"
                variant={TEXT_VARIANTS.BODY_LIGHT}
              >
                {t('lessons.editor.reveal.content_scrolls')}
              </Text>
            }
            checked={scrollable}
            className="EditorElement-options"
            onChange={e => onScrollChange(e.target.checked)}
          />
        )}
      </div>
      <div className="EditorElement-body">
        <TextEditor
          id={`reveal_${id}_subject`}
          variant={TEXT_EDITOR_VARIANTS.CONDENSED}
          onChange={onSubjectChange}
          defaultValue={subject}
          placeholder={t('lessons.editor.reveal.subject')}
        />
        <Text
          variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
          component={TextArea}
          className="EditorElement-input EditorElement-input--small"
          placeholder={t('lessons.editor.reveal.secondary_subject')}
          value={unescape(instruction)}
          onChange={value =>
            debouncedHandleUpdate({ instruction: escape(value) })
          }
          singleLine
        />
        <ImageView
          className="RevealElement-image"
          labelClassName="RevealElement-label"
          label={t('lessons.editor.reveal.content_image.label')}
          hint={t('lessons.editor.reveal.content_image.hint')}
          id={id}
          image={image}
          imageType="content"
          handleImageChange={handleImageChange}
          toggleAddToImageLibrary={toggleAddToImageLibrary}
        />
        <TextEditor
          id={`reveal_${id}_body`}
          variant={TEXT_EDITOR_VARIANTS.CONDENSED}
          onChange={onBodyChange}
          defaultValue={body}
          placeholder={t('lessons.editor.reveal.body')}
        />
      </div>
      <div className="EditorElement-footer bb-0 bdrs-0">
        <div className="EditorElement-hint">
          {t('lessons.editor.reveal.body_hint')}
        </div>
      </div>
      <ImageView
        className="RevealElement-image RevealElement-footer"
        labelClassName="RevealElement-label"
        label={t('lessons.editor.reveal.alt_content_image.label')}
        hint={t('lessons.editor.reveal.alt_content_image.hint')}
        id={`alt_${id}`}
        image={altImage}
        imageType="alt_content"
        handleImageChange={handleImageChange}
        toggleAddToImageLibrary={toggleAddToImageLibrary}
      />
    </div>
  );
}

export default ElementBase(RevealElement);
